import { Fragment } from 'react';

import { EmailOutline, PersonOutline } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Box, Stack, Typography } from '@lyearn/ui';
import { Link } from '@/components/Link';
import * as User from '@/components/User';
import { getLearnerCoverBackground } from '@/modules/learner/utils/cover';
import { LearnerProfileDetailsFragment } from '@/modules/learner/views/LearnerProfile/graphql/fragments/generated/LearnerProfileDetails';
import { h190 } from '@/modules/learner/views/LearnerProfile/styles';
import { Maybe, UserStatusEnum } from '@/types/schema';

interface Params {
  Icon: typeof PersonOutline;
  label?: Maybe<string>;
  to?: string;
}

export const renderLearnerInfoItem = ({ Icon, label, to }: Params, index: number) => {
  const content = (
    <Stack alignItems="center" gapX={12}>
      <Icon className="h-20 w-20" fillColorToken="icon-primary" />
      <Typography className="truncate group-hover:text-text-secondary" variant="body-short-02">
        {label}
      </Typography>
    </Stack>
  );

  return to ? (
    <Link key={index} className="group" target="_blank" to={to}>
      {content}
    </Link>
  ) : (
    <Fragment key={index}>{content}</Fragment>
  );
};

const AVATAR_CLASSES = { border: 'border-4 border-solid border-bg-primary' };

type RenderLearnerCoverParams = { id: string; status?: UserStatusEnum; className?: string };

export const renderLearnerCover = (learner: RenderLearnerCoverParams) => {
  const { id, status, className } = learner;

  if (status === UserStatusEnum.Invited) {
    return <Box className={cn(h190, 'bg-bg-secondary', className)} />;
  }

  return (
    <Box className={cn(h190, className)} style={{ background: getLearnerCoverBackground(id) }} />
  );
};

export const renderLearnerProfilePicture = ({ status }: LearnerProfileDetailsFragment) => {
  if (status === UserStatusEnum.Invited) {
    return (
      <Stack
        alignItems="center"
        className="-mt-40 h-80 w-80 flex-shrink-0 rounded-full border border-dashed border-border-dark bg-bg-primary"
        justifyContent="center">
        <EmailOutline className="h-32 w-32" fillColorToken="icon-placeholder" />
      </Stack>
    );
  }

  return <User.ProfilePicture className="-mt-44 -ms-4" classes={AVATAR_CLASSES} size={88} />;
};
